/**
 * @file grid-meta.ts
 * @description «В ряд на ПК» у сеток элементов секции — реестр по видам
 * (phase19.16, партия SB120, этап 3б; uiux-sb120-stage3 §3 «Плитки и
 * категории», «Сетки остальных видов»; данные db-design-sb120-stage3 §1;
 * пределы — shared `SECTION_LOOK_LIMITS`, наборы — element-grid.ts).
 *
 * На вид: подсказки-ступени и сколько в ряд на планшете (768–1279) —
 * строкой под полем. Планшет — по классам витрины (apps/web, сверено
 * 2026-10-18), 5–6 — как при 4 у этого вида (новые значения 3б):
 *  - плитки и категории — shared `tabletColsFor` (категории не больше 3, SB24);
 *  - товары: до 3 столько же, 4–6 → 3 (`md:grid-cols-3 xl:grid-cols-4`);
 *  - галерея и цифры: до 4 столько же, 5–6 → 4 (`md:grid-cols-N`);
 *  - объявления-карточки: до 3 столько же, 4–6 → 2 (`md:grid-cols-2 xl:…`);
 *  - статьи: 1 → 1, дальше 2 (`md:grid-cols-2 xl:grid-cols-3`);
 *  - отзывы сеткой — shared `reviewsTabletCols` (не больше 2); лентой —
 *    ширины карточки shared (`reviewsPerRowHint`);
 *  - похожие товары: до 2 столько же, иначе 2, а с 1024 — сколько на ПК
 *    (`sm:grid-cols-2`, с `lg` — `--g-xl = repeat(perRow)`, без предела).
 * Допустимые значения — наборы shared (`GRID_PER_ROW`, `TILE_GRID_COLS`,
 * `REVIEW_PER_ROW`) передаёт панель: так значение остаётся литералом схемы.
 * Новый вид сетки = запись здесь (OCP), без ветки в поле.
 */

import { REVIEW_STRIP_PHONE_WIDTH, TILE_COLS_HINTS, reviewStripTabletWidth, reviewStripWidth, reviewsTabletCols, tabletColsFor } from "@chaiberi/shared";

import { formatNumber } from "../site-fields/number-input";

/** Виды с полем «в ряд». */
export type GridKind = "tiles" | "categories" | "products" | "gallery" | "announcements" | "stats" | "articles" | "reviews" | "product-related";

export interface GridMeta {
  /** Ступени-подсказки под ползунком. */
  hints: readonly number[];
  /** Сколько в ряд на планшете словами (без «на планшете»). */
  tablet: (n: number) => string;
}

const CARD_HINTS = [2, 3, 4] as const;
const upTo = (cap: number, over = cap) => (n: number) => String(n <= cap ? n : over);

export const GRID_META: Readonly<Record<GridKind, GridMeta>> = {
  tiles: { hints: TILE_COLS_HINTS, tablet: (n) => String(tabletColsFor(n, "tiles")) },
  categories: { hints: TILE_COLS_HINTS, tablet: (n) => String(tabletColsFor(n, "categories")) },
  products: { hints: CARD_HINTS, tablet: upTo(3) },
  gallery: { hints: CARD_HINTS, tablet: upTo(4) },
  stats: { hints: CARD_HINTS, tablet: upTo(4) },
  announcements: { hints: CARD_HINTS, tablet: upTo(3, 2) },
  articles: { hints: CARD_HINTS, tablet: upTo(1, 2) },
  reviews: { hints: CARD_HINTS, tablet: (n) => String(reviewsTabletCols(n)) },
  "product-related": {
    hints: CARD_HINTS,
    tablet: (n) => (n <= 2 ? String(n) : `2, с 1024 px — ${n}`),
  },
};

/**
 * Подсказка «в ряд» у отзывов лентой (uiux-sb120-stage3 §3): ширины карточки
 * — из shared (`reviewStripWidth`, `reviewStripTabletWidth`,
 * `REVIEW_STRIP_PHONE_WIDTH`), здесь только слова.
 */
export function reviewsPerRowHint(perRow: number): string {
  const pc = formatNumber(reviewStripWidth(perRow), 1);
  return `ПК: ширина карточки в ленте 96/${perRow} % (${pc} %); на планшете ${formatNumber(reviewStripTabletWidth(perRow), 1)} %, на телефоне ${REVIEW_STRIP_PHONE_WIDTH} %.`;
}
